import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, User, Globe, ThumbsUp, ThumbsDown, Send, ArrowLeft, Bot, Search, Filter, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import './ConversationHistory.css';
import './ViewConversationsMockup.css';

// Preprocess markdown content to convert [ ... ] to \[ ... \] for block math equations
// OpenAI sometimes outputs equations in [ ... ] format, we convert to \[ ... \] which MathJax natively supports
const preprocessMathContent = (content) => {
  if (!content || typeof content !== 'string') return content;
  
  // Convert [ ... ] patterns that contain LaTeX to \[ ... \] format (MathJax block math)
  // Match [ followed by content until ], but only if content contains LaTeX commands
  return content.replace(/\[\s*([^\]]+)\s*\]/g, (match, equation) => {
    const trimmed = equation.trim();
    // Check if this contains LaTeX commands (backslash followed by letters like \text, \frac, etc.)
    if (/\\[a-zA-Z]/.test(trimmed)) {
      // Convert to \[ ... \] format (MathJax's native block math delimiter)
      return `\\[${trimmed}\\]`;
    }
    return match;
  });
};

// Fix malformed markdown links in text before rendering
// Handles cases where URLs contain markdown syntax like ]( or %5D(
const fixMalformedMarkdownLinks = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  let fixed = text;
  
  // Pattern 1: Handle [text](url](url)) or [text](url%5D(url)) with extra closing parens
  fixed = fixed.replace(/\[([^\]]*?)\]\((https?:\/\/[^\)]*?)(?:%5D\(|]\([^\)]*?\)+)\)+/g, (match, linkText, url) => {
    const cleanUrl = url.split(/%5D\(|]\(/)[0].trim();
    const cleanLinkText = linkText.replace(/^\[+/, '').trim();
    return `[${cleanLinkText}](${cleanUrl})`;
  });
  
  // Pattern 2: Handle cases with extra leading brackets: [[text](url](url))
  fixed = fixed.replace(/\[+([^\]]*?)\]\((https?:\/\/[^\)]*?)(?:%5D\(|]\([^\)]*?\)+)\)+/g, (match, linkText, url) => {
    const cleanUrl = url.split(/%5D\(|]\(/)[0].trim();
    const cleanLinkText = linkText.replace(/^\[+/, '').trim();
    return `[${cleanLinkText}](${cleanUrl})`;
  });
  
  // Pattern 3: Clean up any remaining stray brackets/parens around links
  // Remove leading [ that appears right before a markdown link pattern
  fixed = fixed.replace(/\[(\[([^\]]+)\]\(https?:\/\/[^\)]+\))/g, '$1');
  // Remove trailing ) that appears right after a markdown link's closing paren
  // Match: [text](url)) -> [text](url)
  fixed = fixed.replace(/(\[([^\]]+)\]\(https?:\/\/[^\)]+\))\)+/g, (match) => {
    // Only keep one closing paren (the one that closes the markdown link)
    const firstClose = match.indexOf(')');
    return match.substring(0, firstClose + 1);
  });
  
  return fixed;
};

// Fix malformed hrefs that contain markdown syntax (e.g., "url%5D(url)" -> "url")
const fixMalformedHref = (href) => {
  if (!href || typeof href !== 'string') return href;
  // Check if href contains the pattern %5D( which is URL-encoded ](
  const malformedPattern = /%5D\(/;
  if (malformedPattern.test(href)) {
    // Extract just the URL part before the malformed markdown syntax
    return href.split(malformedPattern)[0];
  }
  // Also check for literal ]( pattern (in case it's not URL-encoded)
  const literalPattern = /\]\(/;
  if (literalPattern.test(href)) {
    return href.split(literalPattern)[0];
  }
  return href;
};

const ConversationHistory = () => {
  const { user, getToken } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, direct, embedded
  const [ownerResponse, setOwnerResponse] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState({}); // messageId -> 'up' | 'down'
  const [messageFeedbackIds, setMessageFeedbackIds] = useState({}); // messageId -> feedbackId (for deletion)
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedMessageForCorrection, setSelectedMessageForCorrection] = useState(null);
  const [correctionText, setCorrectionText] = useState('');
  
  // Advanced filtering states
  const [readStatusFilter, setReadStatusFilter] = useState('all'); // all, read, unread
  const [recencyFilter, setRecencyFilter] = useState('all'); // all, 24h, 7d, 30d
  const [userFilter, setUserFilter] = useState('all'); // all, specific user
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'chat'
  const [reviewedConversations, setReviewedConversations] = useState(new Set());
  
  // Search and filter UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Load reviewed conversations from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('reviewedConversations');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setReviewedConversations(new Set(parsed));
      } catch (error) {
        console.error('Error loading reviewed conversations:', error);
      }
    }
  }, []);

  // Save reviewed conversations to localStorage when it changes
  useEffect(() => {
    if (reviewedConversations.size > 0) {
      localStorage.setItem('reviewedConversations', JSON.stringify([...reviewedConversations]));
      // Dispatch custom event to notify other components in the same tab
      window.dispatchEvent(new CustomEvent('reviewedConversationsUpdated'));
    }
  }, [reviewedConversations]);

  const fetchConversations = useCallback(async () => {
    if (!user?.agent?.id) return;
    
    try {
      const token = await getToken();
      if (!token) return;
      
      const response = await fetch(`/api/agents/${user.agent.id}/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched conversations for agent', user.agent.id, ':', data.conversations?.length || 0);
        console.log('Raw conversations data:', data.conversations);
        
        // Debug: Check for conversation 1031 specifically
        const conv1031 = data.conversations?.find(conv => conv.id === 1031);
        console.log('Conversation 1031 found:', conv1031);
        
        // Debug: Check all embedded conversations
        const embeddedConvs = data.conversations?.filter(conv => conv.is_embedded === true);
        console.log('Embedded conversations found:', embeddedConvs?.length || 0);
        console.log('Embedded conversation IDs:', embeddedConvs?.map(c => c.id) || []);
        
        // User data is now included in the response from the backend (optimized query)
        const conversationsList = data.conversations || [];
        setConversations(conversationsList);
        
        // Calculate and dispatch unread count for sidepanel badge
        const saved = localStorage.getItem('reviewedConversations');
        const reviewedSet = saved ? new Set(JSON.parse(saved)) : new Set();
        const unreadCount = conversationsList.filter(conv => {
          const hasMessages = (conv.message_count || 0) > 0;
          const isReviewed = reviewedSet.has(conv.id);
          return hasMessages && !isReviewed;
        }).length;
        
        // Dispatch event with unread count for sidepanel
        window.dispatchEvent(new CustomEvent('viewConversationsUnreadCount', { 
          detail: { count: unreadCount, conversations: conversationsList } 
        }));
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.agent?.id]);

  useEffect(() => {
    if (user?.agent?.id) {
      fetchConversations();
    }
  }, [user?.agent?.id, fetchConversations]);

  // Render MathJax equations after messages update in chat detail view
  useEffect(() => {
    if (viewMode === 'chat' && selectedConversation?.messages) {
      if (typeof window !== 'undefined' && window.MathJax && window.MathJax.typesetPromise) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          window.MathJax.typesetPromise().catch((err) => {
            console.error('MathJax typeset error:', err);
          });
        }, 100);
      }
    }
  }, [viewMode, selectedConversation?.messages]);

  // Listen for navigation to conversations list from sidepanel
  useEffect(() => {
    const handleNavigateToConversations = () => {
      // If we're in chat view, reset to table view
      if (viewMode === 'chat') {
        setViewMode('table');
        setSelectedConversation(null);
      }
    };

    window.addEventListener('navigateToConversations', handleNavigateToConversations);
    return () => {
      window.removeEventListener('navigateToConversations', handleNavigateToConversations);
    };
  }, [viewMode]);

  const loadConversationFeedback = async (conversationId) => {
    if (!user?.agent?.id) return {};
    
    try {
      const token = await getToken();
      if (!token) return {};
      
      const response = await fetch(`/api/agents/feedback/${user.agent.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter feedback for this conversation and create a map by message ID
        const conversationFeedback = {};
        data.forEach(feedback => {
          if (feedback.conversation_id === conversationId) {
            conversationFeedback[feedback.message_id] = {
              rating: feedback.rating,
              feedbackType: feedback.feedback_type,
              feedbackId: feedback.id // Include feedback ID for deletion
            };
          }
        });
        return conversationFeedback;
      }
    } catch (error) {
      console.error('Error loading conversation feedback:', error);
    }
    return {};
  };

  const fetchConversationDetails = async (conversationId) => {
    if (!user?.agent?.id) return;
    
    try {
      const token = await getToken();
      if (!token) return;
      
      const response = await fetch(`/api/agents/${user.agent.id}/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedConversation(data.conversation);
        setViewMode('chat');
        
        // Load feedback for this conversation and initialize state
        const conversationFeedback = await loadConversationFeedback(conversationId);
        const feedbackState = {};
        const feedbackIdsState = {};
        
        if (data.conversation?.messages) {
          data.conversation.messages.forEach(msg => {
            const msgIdKey = typeof msg.id === 'string' ? parseInt(msg.id) : msg.id;
            if ((msg.role === 'assistant' || msg.role === 'bot') && !msg.is_owner_response && conversationFeedback[msg.id]) {
              const feedback = conversationFeedback[msg.id];
              if (feedback.rating > 0) {
                feedbackState[msgIdKey] = 'up';
                feedbackIdsState[msgIdKey] = feedback.feedbackId;
              } else if (feedback.rating < 0) {
                feedbackState[msgIdKey] = 'down';
                feedbackIdsState[msgIdKey] = feedback.feedbackId;
              }
            }
          });
        }
        
        setMessageFeedback(feedbackState);
        setMessageFeedbackIds(feedbackIdsState);
        
        // Mark conversation as reviewed
        const newReviewed = new Set([...reviewedConversations, conversationId]);
        setReviewedConversations(newReviewed);
        
        // Recalculate and dispatch unread count after marking as reviewed
        const saved = localStorage.getItem('reviewedConversations');
        const reviewedSet = saved ? new Set(JSON.parse(saved)) : new Set();
        reviewedSet.add(conversationId);
        const unreadCount = conversations.filter(conv => {
          const hasMessages = (conv.message_count || 0) > 0;
          const isReviewed = reviewedSet.has(conv.id);
          return hasMessages && !isReviewed;
        }).length;
        
        window.dispatchEvent(new CustomEvent('viewConversationsUnreadCount', { 
          detail: { count: unreadCount, conversations: conversations } 
        }));
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  const handleBackToTable = () => {
    setViewMode('table');
    setSelectedConversation(null);
  };

  const getFirstMessagePreview = (conversation) => {
    // Use the first_user_message field from the API (optimized)
    if (conversation.first_user_message) {
      return conversation.first_user_message.length > 80 
        ? conversation.first_user_message.substring(0, 80) + '...'
        : conversation.first_user_message;
    }
    
    // Fallback to messages array if first_user_message is not available
    if (conversation.messages && conversation.messages.length > 0) {
      const firstUserMessage = conversation.messages.find(msg => msg.role === 'user');
      if (firstUserMessage) {
        return firstUserMessage.content.length > 80 
          ? firstUserMessage.content.substring(0, 80) + '...'
          : firstUserMessage.content;
      }
    }
    
    return 'No messages yet';
  };

  const getUserDisplay = (conversation) => {
    // For embedded conversations, show "Embedded"
    if (conversation.is_embedded) {
      return {
        name: 'Embedded',
        avatar: null
      };
    }
    
    // For direct conversations, check if we have user data from the users table
    if (conversation.user_data) {
      return {
        name: conversation.user_data.name || 'Unknown User',
        avatar: conversation.user_data.picture || null
      };
    }
    
    // Fallback to conversation-level user info if available
    if (conversation.user_name) {
      return {
        name: conversation.user_name,
        avatar: conversation.user_picture
      };
    }
    
    // Final fallback to Anonymous User
    return {
      name: 'Anonymous User',
      avatar: null
    };
  };

  const getNewMessageIndicator = (conversation) => {
    // Show notification dot if conversation has messages and hasn't been reviewed
    const hasMessages = (conversation.message_count || 0) > 0;
    const isReviewed = reviewedConversations.has(conversation.id);
    return hasMessages && !isReviewed;
  };

  const getOwnerResponseStatus = (conversation) => {
    const state = conversation.owner_response_state || 'not_requested';
    const engagementFee = conversation.engagement_fee_cents || 0;
    
    switch (state) {
      case 'requested':
        return (
          <div className="owner-response-requested">
            <span className="status-badge requested">Requested</span>
            {conversation.requested_at && (
              <div className="timestamp">
                {formatDate(conversation.requested_at)}
              </div>
            )}
            {engagementFee > 0 && (
              <div className="fee">
                ${(engagementFee / 100).toFixed(2)}
              </div>
            )}
          </div>
        );
      case 'fulfilled':
        return (
          <div className="owner-response-fulfilled">
            <span className="status-badge fulfilled">Completed</span>
            {conversation.fulfilled_at && (
              <div className="timestamp">
                {formatDate(conversation.fulfilled_at)}
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className="owner-response-not-requested">
            <span className="status-badge not-requested">Not Requested</span>
          </div>
        );
    }
  };

  const sendOwnerResponse = async () => {
    if (!ownerResponse.trim() || !selectedConversation || sendingResponse) return;
    
    setSendingResponse(true);
    try {
      const token = await getToken();
      if (!token) {
        setSendingResponse(false);
        return;
      }
      
      const response = await fetch(`/api/agents/${user.agent.id}/conversations/${selectedConversation.id}/owner-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: ownerResponse.trim() })
      });
      
      if (response.ok) {
        const result = await response.json();
        setOwnerResponse('');
        
        // If owner engagement was fulfilled, update the conversation state
        if (result.owner_engagement_fulfilled) {
          console.log('✅ [CONVERSATION_HISTORY] Owner engagement fulfilled');
          // Update the conversation in the list
          setConversations(prev => prev.map(conv => 
            conv.id === selectedConversation.id 
              ? { ...conv, owner_response_state: 'fulfilled', fulfilled_at: new Date().toISOString() }
              : conv
          ));
        }
        
        // Refresh the conversation to show the new response
        await fetchConversationDetails(selectedConversation.id);
      }
    } catch (error) {
      console.error('Error sending owner response:', error);
    } finally {
      setSendingResponse(false);
    }
  };

  const sendMessageFeedback = async (messageId, feedback) => {
    // Normalize messageId to number for consistent key matching
    const messageIdKey = typeof messageId === 'string' ? parseInt(messageId) : messageId;
    const currentFeedback = messageFeedback[messageIdKey];
    const feedbackId = messageFeedbackIds[messageIdKey];
    
    // If button is already selected (green), make it white and delete feedback
    if (currentFeedback === feedback) {
      // Toggle off - set to null so React detects the change
      setMessageFeedback(prev => ({ ...prev, [messageIdKey]: null }));
      setMessageFeedbackIds(prev => ({ ...prev, [messageIdKey]: null }));
      
      // Delete from backend (async)
      if (feedbackId) {
        const token = await getToken();
        if (token) {
          fetch(`/api/agents/feedback/${feedbackId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ agentId: user.agent.id })
          }).catch(err => console.error('Error deleting feedback:', err));
        }
      }
      return;
    }
    
    // For thumbs down, open correction modal
    if (feedback === 'down') {
      const message = selectedConversation?.messages?.find(m => {
        const mIdKey = typeof m.id === 'string' ? parseInt(m.id) : m.id;
        return mIdKey === messageIdKey;
      });
      if (message) {
        setSelectedMessageForCorrection(message);
        setShowCorrectionModal(true);
      }
      return;
    }
    
    // For thumbs up, make it green and add feedback
    if (feedback === 'up') {
      // Update UI immediately - add to state
      setMessageFeedback(prev => ({ ...prev, [messageIdKey]: feedback }));
      
      // Add to backend
      try {
        const token = await getToken();
        if (!token) return;
        
        const response = await fetch(`/api/agents/${user.agent.id}/messages/${messageId}/feedback`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ feedback })
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.feedbackId) {
            setMessageFeedbackIds(prev => ({ ...prev, [messageIdKey]: result.feedbackId }));
          }
        } else {
          // Revert on failure
          setMessageFeedback(prev => {
            const updated = { ...prev };
            delete updated[messageIdKey];
            return updated;
          });
        }
      } catch (error) {
        console.error('Error sending feedback:', error);
        // Revert on error
        setMessageFeedback(prev => {
          const updated = { ...prev };
          delete updated[messageIdKey];
          return updated;
        });
      }
    }
  };


  console.log('Starting filter with:', {
    totalConversations: conversations.length,
    currentFilter: filter,
    searchQuery: searchQuery,
    readStatusFilter: readStatusFilter,
    recencyFilter: recencyFilter,
    userFilter: userFilter
  });
  
  const filteredConversations = conversations.filter(conv => {
    // Show external conversations only (embedded or direct from external users)
    // External conversations are: is_embedded = true OR (is_embedded = false AND user_id is NOT NULL)
    // Owner conversations are: is_embedded = false AND user_id is NULL (owner's own conversations)
    
    const isExternalConversation = conv.is_embedded || (!conv.is_embedded && conv.user_id);
    
    // Debug logging for conversation 1031
    if (conv.id === 1031) {
      console.log('Filtering conversation 1031:', {
        id: conv.id,
        is_embedded: conv.is_embedded,
        user_id: conv.user_id,
        isExternalConversation: isExternalConversation,
        title: conv.title
      });
    }
    
    // Only show external conversations
    if (!isExternalConversation) {
      if (conv.id === 1031) {
        console.log('Conversation 1031 filtered out at isExternalConversation check');
      }
      return false;
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const firstMessage = getFirstMessagePreview(conv).toLowerCase();
      const userDisplay = getUserDisplay(conv);
      const userName = userDisplay.name.toLowerCase();
      
      if (!firstMessage.includes(query) && !userName.includes(query)) {
        return false;
      }
    }
    
    // Filter by conversation type (all, direct, embedded)
    if (filter === 'all') {
      // Continue to other filters
    } else if (filter === 'direct') {
      if (conv.is_embedded) return false;
    } else if (filter === 'embedded') {
      if (!conv.is_embedded) return false;
    }
    
    // Filter by read status
    if (readStatusFilter === 'read') {
      if (!reviewedConversations.has(conv.id)) return false;
    } else if (readStatusFilter === 'unread') {
      if (reviewedConversations.has(conv.id)) return false;
    }
    
    // Filter by recency
    if (recencyFilter !== 'all') {
      const now = new Date();
      const convDate = new Date(conv.created_at);
      const hoursDiff = (now - convDate) / (1000 * 60 * 60);
      
      if (recencyFilter === '24h' && hoursDiff > 24) return false;
      if (recencyFilter === '7d' && hoursDiff > 24 * 7) return false;
      if (recencyFilter === '30d' && hoursDiff > 24 * 30) return false;
    }
    
    // Filter by user
    if (userFilter !== 'all') {
      const userDisplay = getUserDisplay(conv);
      if (userDisplay.name !== userFilter) return false;
    }
    
    return true;
  });
  
  // Debug: Check if conversation 1031 made it through filtering
  const conv1031AfterFilter = filteredConversations.find(conv => conv.id === 1031);
  console.log('Conversation 1031 after filtering:', conv1031AfterFilter ? 'FOUND' : 'NOT FOUND');
  console.log('Total conversations after filtering:', filteredConversations.length);
  
  console.log('Filtered conversations count:', filteredConversations.length);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getConversationType = (conversation) => {
    if (conversation.is_embedded) {
      return { type: 'Embedded', icon: Globe, color: 'blue' };
    }
    return { type: 'Direct', icon: User, color: 'green' };
  };

  // Get unique users for filter dropdown
  const getUniqueUsers = () => {
    const users = new Set();
    conversations.forEach(conv => {
      const userDisplay = getUserDisplay(conv);
      users.add(userDisplay.name);
    });
    return Array.from(users).sort();
  };

  if (loading) {
    return (
      <div className="conversation-history">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading conversations...</p>
        </div>
      </div>
    );
  }

  // Chat Detail View
  if (viewMode === 'chat' && selectedConversation) {
    return (
      <div className="view-conversations-mockup" data-theme="dark">
        {/* Header */}
        <div className="view-conversations-header">
          <button className="view-conversations-back-btn" onClick={handleBackToTable}>
            <ArrowLeft size={16} />
            Back to Conversations
          </button>
          <div className="view-conversations-info">
            <h2>Conversation with {getUserDisplay(selectedConversation).name}</h2>
          </div>
        </div>

        {/* Messages List */}
        <div className="view-conversations-messages">
          {selectedConversation.messages?.map((message, index) => {
            const isOwnerResponse = message.is_owner_response || message.role === 'owner';
            const isAssistant = message.role === 'assistant';
            const isUser = message.role === 'user';
            const isBotMessage = isAssistant || isOwnerResponse; // Bot messages include both assistant and owner responses
            return (
            <div key={index} className={`view-conversations-message ${isUser ? 'user' : 'bot'}`}>
              {isUser && (
                <div className="view-conversations-avatar">
                  <div className="user-avatar-mockup">
                    {getUserDisplay(selectedConversation).name.charAt(0).toUpperCase()}
                  </div>
                </div>
              )}
              <div className="view-conversations-message-content">
                <div className="view-conversations-message-text">
                  <div className="view-conversations-message-header">
                    <span className="message-sender">
                      {isUser ? getUserDisplay(selectedConversation).name : 
                       isOwnerResponse ? 'Owner' : 'Agent'}
                    </span>
                    {isAssistant && !isOwnerResponse && (
                      <div className="view-conversations-feedback-icons">
                        <ThumbsUp 
                          className="view-conversations-feedback-icon thumbs-up"
                          color={(() => {
                            const key = typeof message.id === 'string' ? parseInt(message.id) : message.id;
                            return messageFeedback[key] === 'up' ? 'var(--accent-success)' : 'white';
                          })()}
                          onClick={() => sendMessageFeedback(message.id, 'up')}
                          size={18}
                        />
                        <ThumbsDown 
                          className="view-conversations-feedback-icon thumbs-down"
                          color={(() => {
                            const key = typeof message.id === 'string' ? parseInt(message.id) : message.id;
                            return messageFeedback[key] === 'down' ? 'var(--accent-error)' : 'white';
                          })()}
                          onClick={() => sendMessageFeedback(message.id, 'down')}
                          size={18}
                        />
                      </div>
                    )}
                  </div>
                  {isBotMessage ? (
                    <div className="markdown-content">
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => {
                            const fixedHref = fixMalformedHref(props.href);
                            return <a {...props} href={fixedHref} target="_blank" rel="noopener noreferrer" />;
                          }
                        }}
                      >
                        {fixMalformedMarkdownLinks(preprocessMathContent(message.content))}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    message.content
                  )}
                </div>
              </div>
              {isBotMessage && (
                <div className="view-conversations-avatar">
                  {isOwnerResponse ? (
                    <div className="user-avatar-mockup">
                      {user?.name?.charAt(0).toUpperCase() || 'O'}
                    </div>
                  ) : (
                    <div className="view-conversations-bot-avatar">
                      <Bot size={20} style={{ display: 'block' }} />
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>

        {/* Owner Jump-in Input */}
        <div className="input-area conversation-history-input">
          <div className="input-container">
            <input
              type="text"
              value={ownerResponse}
              onChange={(e) => setOwnerResponse(e.target.value)}
              placeholder="Respond as owner..."
              className="message-input"
              onKeyPress={(e) => e.key === 'Enter' && sendOwnerResponse()}
              disabled={sendingResponse}
            />
            <button onClick={sendOwnerResponse} className="send-button" disabled={!ownerResponse.trim() || sendingResponse}>
              <Send size={16} />
            </button>
          </div>
        </div>

        {/* Correction Modal */}
        {showCorrectionModal && (
          <div className="modal-overlay" onClick={() => {
            setShowCorrectionModal(false);
            setSelectedMessageForCorrection(null);
            setCorrectionText('');
          }}>
            <div className="correction-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Provide Correction</h3>
                <button className="close-btn" onClick={() => {
                  setShowCorrectionModal(false);
                  setSelectedMessageForCorrection(null);
                  setCorrectionText('');
                }}>×</button>
              </div>
              <div className="modal-content">
                <div className="original-response">
                  <strong>Original Response:</strong>
                  <div className="response-text">{selectedMessageForCorrection?.content}</div>
                </div>
                <div className="correction-input">
                  <label htmlFor="correction">What should the response have been?</label>
                  <textarea
                    id="correction"
                    value={correctionText}
                    onChange={(e) => setCorrectionText(e.target.value)}
                    placeholder="Enter the correct response..."
                    rows={4}
                    style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '6px',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      resize: 'none',
                      width: '100%'
                    }}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => {
                  setShowCorrectionModal(false);
                  setSelectedMessageForCorrection(null);
                  setCorrectionText('');
                }}>
                  Cancel
                </button>
                <button 
                  className="submit-btn" 
                  onClick={async () => {
                    if (!correctionText.trim() || !selectedMessageForCorrection) return;
                    try {
                      const token = await getToken();
                      if (!token) return;
                      const messageId = selectedMessageForCorrection.id;
                      const messageIdKey = typeof messageId === 'string' ? parseInt(messageId) : messageId;
                      const response = await fetch(`/api/agents/${user.agent.id}/messages/${messageId}/feedback`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          feedback: 'down',
                          correctedResponse: correctionText.trim()
                        })
                      });
                      if (response.ok) {
                        const result = await response.json();
                        setMessageFeedback(prev => ({ ...prev, [messageIdKey]: 'down' }));
                        if (result.feedbackId) {
                          setMessageFeedbackIds(prev => ({ ...prev, [messageIdKey]: result.feedbackId }));
                        }
                        setShowCorrectionModal(false);
                        setSelectedMessageForCorrection(null);
                        setCorrectionText('');
                      }
                    } catch (error) {
                      console.error('Error submitting correction:', error);
                    }
                  }}
                  disabled={!correctionText.trim()}
                >
                  Submit Correction
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Table View
  return (
    <div className="conversation-history">
      <div className="history-header">
        <h2>Conversations</h2>
      </div>


      <div className="conversations-table-container">
        {/* Search and Filter Bar */}
        <div className="table-controls">
          <div className="search-container">
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          <div className="table-actions">
            <span className="results-count">{filteredConversations.length} conversations</span>
            <button 
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} />
              Filters
            </button>
            <button className="refresh-btn" onClick={fetchConversations}>
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Collapsible Filter Panel */}
        {showFilters && (
          <div className="filter-panel">
            <div className="filter-grid">
              <div className="filter-group">
                <label>Type</label>
                <select 
                  value={filter} 
                  onChange={(e) => setFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Conversations</option>
                  <option value="direct">Direct Chats</option>
                  <option value="embedded">Embedded Chats</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Read Status</label>
                <select 
                  value={readStatusFilter} 
                  onChange={(e) => setReadStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Recency</label>
                <select 
                  value={recencyFilter} 
                  onChange={(e) => setRecencyFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Time</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>User</label>
                <select 
                  value={userFilter} 
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Users</option>
                  {getUniqueUsers().map(user => (
                    <option key={user} value={user}>{user}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="filter-actions">
              <button 
                className="clear-filters-btn"
                onClick={() => {
                  setFilter('all');
                  setReadStatusFilter('all');
                  setRecencyFilter('all');
                  setUserFilter('all');
                  setSearchQuery('');
                }}
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}

        {filteredConversations.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={48} className="empty-icon" />
            {searchQuery.trim() || filter !== 'all' || readStatusFilter !== 'all' || recencyFilter !== 'all' || userFilter !== 'all' ? (
              <>
                <h3>No results found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </>
            ) : (
              <>
                <h3>No conversations yet</h3>
                <p>Conversations will appear here once people start chatting with your agent</p>
              </>
            )}
          </div>
        ) : (
          <div className="conversations-table-container">
            <table className="conversations-table">
              <thead>
                <tr>
                  <th className="status-column"></th>
                  <th>Timestamp</th>
                  <th>First Message</th>
                  <th className="user-column-header">User</th>
                  <th className="messages-column-header">Messages</th>
                  {user?.beta_tester && <th className="owner-response-column-header">Owner Response</th>}
                  <th className="actions-column-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredConversations.map((conversation) => {
                  const userDisplay = getUserDisplay(conversation);
                  const hasNew = getNewMessageIndicator(conversation);
                  const conversationType = getConversationType(conversation);
                  
                  
                  return (
                    <tr 
                      key={conversation.id}
                      className="conversation-row"
                      onClick={() => fetchConversationDetails(conversation.id)}
                    >
                      <td className="status-column">
                        <div className="status-indicator">
                          {hasNew && (
                            <div className={`new-message-dot ${conversationType.color}`}></div>
                          )}
                        </div>
                      </td>
                      <td className="timestamp-column">
                        {formatDate(conversation.created_at)}
                      </td>
                      <td className="message-preview-column">
                        <div className="message-preview">
                          {getFirstMessagePreview(conversation)}
                        </div>
                      </td>
                      <td className="user-column">
                        <span className="user-name">{userDisplay.name}</span>
                      </td>
                      <td className="messages-count-column">
                        <span className="messages-count">{conversation.message_count || 0}</span>
                      </td>
                      {user?.beta_tester && (
                        <td className="owner-response-column">
                          <div className="owner-response-status">
                            {getOwnerResponseStatus(conversation)}
                          </div>
                        </td>
                      )}
                      <td className="actions-column">
                        <button 
                          className="view-conversation-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchConversationDetails(conversation.id);
                          }}
                        >
                          View Conversation
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationHistory;
